import { signal } from '@preact/signals'
import { deviceStorage, forgetUnnamedSchedule, useKid } from '../platform/web'
import { forgetChild } from '../api/account'
import type { Storage } from '../platform/storage'
import { KidStorage, currentKid, kidPrefix, kids, loadKids, NO_KID, saveKids, UNNAMED_KEY, type Kid } from './children'
import { KEY } from './keys'
import { loadProfile, saveProfile } from './profile'
import { loadEvents } from './events'
import { loadAllNotes } from './notes'
import { loadWindows } from './history'

/**
 * Подготовка списка малышей при запуске: чтение списка и выбор текущего.
 * Вызывается до чтения данных, иначе сторы прочитают пустоту.
 */
export async function prepareKids(): Promise<void> {
  const base = deviceStorage()
  let known = await loadKids(base)
  if (known.list.length === 0) {
    const спасённые = await восстановитьСписок(base)
    if (спасённые.length > 0) {
      await saveKids(base, спасённые, спасённые[0]!.id)
      known = { list: спасённые, current: спасённые[0]!.id }
    }
  }
  kids.value = known.list
  currentKid.value = known.current
  if (known.current !== null) useKid(known.current)
}

/**
 * Список малышей пуст, а их данные на месте: собираем список заново по ключам
 * `kid.<номер>.child_name`.
 *
 * Без этого приложение считает, что малыша нет, и показывает анкету. Человек
 * заполняет её заново — на счёте появляется второй такой же «Паша», а прежние
 * записи остаются под старым номером и выглядят пропавшими (случай 12.10.2026).
 */
async function восстановитьСписок(base: Storage): Promise<Kid[]> {
  const found: Kid[] = []
  for (const key of await base.keys()) {
    const m = /^kid\.([^.]+)\.child_name$/.exec(key)
    if (m === null) continue
    const name = await base.get(key)
    if (name !== null && name.trim() !== '') found.push({ id: m[1]!, name })
  }
  return found
}

/**
 * Аккаунт удалён — забываем всех малышей, а не только того, кого показывали.
 * Дальше устройство чистое: следующая анкета ляжет под новым номером.
 */
/**
 * Досказать серверу то, что осталось от переезда на имена: безымянного расписания
 * устройства больше нет. Пока не получилось — метка лежит и ждёт следующего запуска.
 */
export async function settleUnnamedSchedule(program: Parameters<typeof forgetUnnamedSchedule>[0]): Promise<void> {
  const base = deviceStorage()
  if ((await base.get(UNNAMED_KEY)) === null) return
  if (await forgetUnnamedSchedule(program)) await base.remove(UNNAMED_KEY)
}

export async function forgetAllKids(): Promise<void> {
  const base = deviceStorage()
  for (const key of await base.keys()) {
    if (key === 'device_token') continue
    await base.remove(key)
  }
  kids.value = []
  currentKid.value = null
  useKid(NO_KID)
}

/**
 * Строка списка: по имени одного малыша от другого не отличить — у жены заказчика
 * оказалось три «Паши» (12.10.2026). Поэтому рядом с именем показываем дату рождения
 * и последнюю запись, а пустую запись разрешаем убрать.
 */
export interface KidCard {
  id: string
  name: string
  birthMillis: number | null
  /** Окна, записи журнала и заметки вместе. Ноль — в записи ничего нет. */
  records: number
  /** Когда была последняя запись. */
  lastRecord: number | null
}

async function cardOf(base: Storage, kid: Kid): Promise<KidCard> {
  const storage = new KidStorage(base, kid.id)
  const profile = await loadProfile(storage)
  const events = (await loadEvents(storage)).filter((e) => e.deleted !== true)
  const notes = (await loadAllNotes(storage)).filter((n) => n.deleted !== true)
  const windows = await loadWindows(storage)
  const моменты = [...events.map((e) => e.at), ...notes.map((n) => n.time), ...windows]
  return {
    id: kid.id,
    name: profile?.name ?? kid.name,
    birthMillis: profile?.birthMillis ?? null,
    records: events.length + notes.length + windows.length,
    lastRecord: моменты.length === 0 ? null : Math.max(...моменты),
  }
}

/** Что показать в списке малышей. Читает хранилище каждого, поэтому зовут по открытию списка. */
export async function kidCards(): Promise<KidCard[]> {
  const base = deviceStorage()
  const out: KidCard[] = []
  for (const kid of kids.value) out.push(await cardOf(base, kid))
  return out
}

/**
 * Убрать запись, в которой ничего нет: ни окон, ни заметок, ни взвешиваний.
 *
 * Запись с историей не удаляем ни здесь, ни на сервере — потерять её нельзя. Текущую
 * тоже: сначала переключитесь, иначе приложению некого показывать.
 */
export async function removeKid(id: string): Promise<'gone' | 'has_records' | 'failed'> {
  if (id === currentKid.value || !kids.value.some((k) => k.id === id)) return 'failed'
  const base = deviceStorage()
  if ((await cardOf(base, { id, name: '' })).records > 0) return 'has_records'
  const ответ = await forgetChild(id)
  if (ответ !== 'gone') return ответ
  for (const key of await base.keys()) {
    if (key.startsWith(kidPrefix(id))) await base.remove(key)
  }
  await saveKids(base, kids.value.filter((k) => k.id !== id), currentKid.value)
  return 'gone'
}

const ДЕНЬ = 24 * 3600 * 1000
const деньРождения = (at: number) => Math.floor(new Date(at).setHours(0, 0, 0, 0) / ДЕНЬ)
const тоЖеИмя = (a: string, b: string) => a.trim().toLocaleLowerCase() === b.trim().toLocaleLowerCase() && a.trim() !== ''

// Переезда малыша на номер сервера здесь больше нет (был с 12.10 по 28.10.2026). Имя
// ящика не меняется никогда: переименование из трёх шагов было единственным местом, где
// данные могли оказаться под одним именем, а указатель под другим. Серверный номер старых
// малышей лежит рядом в списке — см. `Kid.serverId`.

/** Имя малыша, на которого переключились после слияния двойников: о таком надо сказать. */
export const mergedInto = signal<string | null>(null)

/**
 * Одна анкета, заведённая дважды, сливается в одну.
 *
 * Устройство с чистой памятью (переустановили, очистили сайт, нажали «Добавить второго
 * малыша») заводит малышу новый номер, и на счёте появляется второй такой же «Паша» —
 * пустой. Отличить их в списке невозможно, а история остаётся у первого. Поэтому пустую
 * запись с тем же именем и днём рождения убираем, а вес при рождении из неё переносим:
 * его вписал человек. Запись с историей никогда не трогаем.
 *
 * Возвращает номер малыша, на которого пришлось переключиться, или null.
 */
export async function mergeDuplicateKids(): Promise<string | null> {
  const base = deviceStorage()
  const cards = await kidCards()
  let switched: string | null = null
  for (const пустая of cards) {
    if (пустая.records > 0 || пустая.birthMillis === null) continue
    // Человек сам сказал, что это другой ребёнок, — не сводим, как бы ни совпадало
    if ((await new KidStorage(base, пустая.id).get(KEY.separateChild)) !== null) continue
    const живая = cards.find(
      (c) =>
        c.id !== пустая.id && c.records > 0 && c.birthMillis !== null &&
        деньРождения(c.birthMillis) === деньРождения(пустая.birthMillis!) && тоЖеИмя(c.name, пустая.name),
    )
    if (живая === undefined) continue

    const откуда = await loadProfile(new KidStorage(base, пустая.id))
    const куда = await loadProfile(new KidStorage(base, живая.id))
    if (куда !== null && откуда?.birthWeightGrams !== undefined && куда.birthWeightGrams === undefined) {
      await saveProfile(new KidStorage(base, живая.id), { ...куда, birthWeightGrams: откуда.birthWeightGrams })
    }
    // Текущего удалить нельзя: сначала переходим на того, у кого история
    if (currentKid.value === пустая.id) {
      await saveKids(base, kids.value, живая.id)
      useKid(живая.id)
      switched = живая.id
      mergedInto.value = живая.name
    }
    if ((await removeKid(пустая.id)) !== 'gone' && switched !== null) break
  }
  return switched
}

/** Переключает приложение на другого малыша. Возвращает false, если такого нет. */
export async function switchKid(id: string): Promise<boolean> {
  if (!kids.value.some((k) => k.id === id)) return false
  await saveKids(deviceStorage(), kids.value, id)
  useKid(id)
  return true
}

/**
 * Заводит нового малыша и делает его текущим. Данные пишет уже обычный стор профиля.
 *
 * `separate` — анкету открыл человек кнопкой «Добавить второго малыша». Это и есть
 * его слово, что ребёнок другой: сервер такую запись ни с чем не сведёт, даже если
 * имя, пол и день рождения совпадут с уже заведённым (вопрос заказчика 12.10.2026).
 */
export async function addKid(name: string, id: string, separate = false): Promise<void> {
  const next: Kid[] = [...kids.value, { id, name }]
  await saveKids(deviceStorage(), next, id)
  useKid(id)
  if (separate) await new KidStorage(deviceStorage(), id).set(KEY.separateChild, '1')
}

/** Имя малыша могло измениться в анкете — список держим в согласии с данными. */
export async function renameKid(id: string, name: string): Promise<void> {
  const next = kids.value.map((k) => (k.id === id ? { ...k, name } : k))
  await saveKids(deviceStorage(), next, currentKid.value)
}

/**
 * Профиль сохранён: если малыша ещё нет в списке — завести, если имя изменилось — поправить.
 * Так список и данные не расходятся, даже когда анкету открыли из старой ссылки.
 */
export async function rememberKid(name: string): Promise<void> {
  const id = currentKid.value
  // Малыша без номера не бывает: номер даёт сервер при создании (28.10.2026). Анкета без
  // текущего малыша — ошибка вызова, а не повод придумать номер на телефоне.
  if (id === null) return
  if (kids.value.find((k) => k.id === id)?.name !== name) await renameKid(id, name)
}
